<script>
  import {
    onMount,
    onDestroy,
    createEventDispatcher,
    getContext,
  } from "svelte";
  import { Router, Route } from "svelte-routing";
  import HomePage from "./pages/HomePage.svelte";
  import StorySlidePage from "./pages/StorySlidePage.svelte";
  import StoryGridPage from "./pages/StoryGridPage.svelte";
  import { setContext } from "svelte";
  import { writable, get } from "svelte/store";
  import { storyStore } from "./stores/storyStore.js";
  import HeaderBar from "./components/HeaderBar.svelte";
  import { evidenceStore, searchEvidence, initializeEvidenceStore } from "./stores/evidenceStore";
  import { promptStore } from "./stores/promptStore";
  import { navigate } from "svelte-routing";
  import StoryCanvas from "./components/StoryCanvas.svelte";
  import StoryResearcherPage from "./pages/StoryResearcherPage.svelte";
  import AuthGuard from "./components/AuthGuard.svelte";
  import { authStore } from "./stores/authStore.js";
  import ToastContainer from "./components/ToastContainer.svelte";
  import ManageEvidencePage from "./pages/ManageEvidencePage.svelte";

  const selectedTool = writable("select");
  const selectedSlideIndex = writable(0);
  const selectedItem = writable("elements");

  setContext("selectedTool", selectedTool);
  setContext("selectedSlideIndex", selectedSlideIndex);
  setContext("selectedItem", selectedItem);
  setContext("storyStore", storyStore);
  setContext("authStore", authStore);

  // Create a store for tracking info
  const trackingInfo = writable({
    trackingId: "",
    promptId: null,
    name: ""
  });

  setContext("trackingInfo", trackingInfo);

  // Auto-save functionality
  let autoSaveTimer;
  let lastSaveTime = Date.now();
  const AUTO_SAVE_DELAY = 3000; // 3 seconds delay for auto-save
  
  const storyUnsubscribe = storyStore.subscribe(state => {
    if (state.isDirty && !state.isSaving && state._id) {
      // Only schedule auto-save if enough time has passed since last save
      const now = Date.now();
      if (now - lastSaveTime > AUTO_SAVE_DELAY) {
        // Clear any pending timer
        if (autoSaveTimer) {
          clearTimeout(autoSaveTimer);
        }
        
        // Set a new timer
        autoSaveTimer = setTimeout(async () => {
          console.log("Auto-saving story changes...");
          try {
            await storyStore.saveStory();
            lastSaveTime = Date.now();
            console.log("Auto-save completed successfully");
          } catch (error) {
            console.error("Auto-save failed:", error);
          }
        }, AUTO_SAVE_DELAY);
      }
    }
  });

  // Fetch custom link info by tracking ID
  async function fetchCustomLinkByTrackingId(trackingId) {
    try {
      const response = await fetch(`/api/custom-links/by-tracking-id/${trackingId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch custom link');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching custom link:', error);
      return null;
    }
  }

  // Fallback implementation for fetchPromptById if it doesn't exist in promptStore
  async function fetchPromptByIdFallback(promptId) {
    try {
      console.log('Using fetchPromptByIdFallback with ID:', promptId);
      // Use API service directly or implement similar logic to what should be in promptStore
      const response = await fetch(`/api/prompts/${promptId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch prompt details');
      }
      
      const promptDetails = await response.json();
      
      // Update the store manually
      promptStore.update(store => ({
        ...store,
        selectedPromptId: promptId,
        selectedPromptDetails: promptDetails
      }));
      
      return promptDetails;
    } catch (error) {
      console.error('Fallback prompt fetch failed:', error);
      throw error;
    }
  }

  // Handle user account creation with tracking ID
  const authUnsubscribe = authStore.subscribe(async (authState) => {
    const tid = localStorage.getItem('pending_tracking_id');
    
    // If user just got authenticated and there's a pending tracking ID, save it to user
    if (authState.isAuthenticated && !authState.isLoading && tid) {
      try {
        console.log(`Updating user with tracking ID: ${tid}`);
        
        // Update user with tracking ID
        const response = await fetch('/auth/update-user-tracking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ trackingId: tid })
        });
        
        if (response.ok) {
          // DON'T remove from local storage yet - we'll do that after processing the link
          // localStorage.removeItem('pending_tracking_id');
          console.log('User tracking ID updated successfully');
          
          // Now try to fetch the custom link info and process it
          try {
            const customLink = await fetchCustomLinkByTrackingId(tid);
            
            if (customLink) {
              // Set tracking info in the store
              trackingInfo.set({
                trackingId: tid,
                promptId: customLink.promptId,
                name: customLink.name
              });
              
              // If there's a preset prompt, redirect to /grid with that prompt
              if (customLink.promptId) {
                // First load the prompt into the store
                try {
                  // Check if the function exists in promptStore, otherwise use fallback
                  if (typeof promptStore.fetchPromptById === 'function') {
                    await promptStore.fetchPromptById(customLink.promptId);
                  } else {
                    // Use fallback implementation
                    await fetchPromptByIdFallback(customLink.promptId);
                  }
                  
                  // Then navigate to grid with the prompt pre-selected
                  navigate(`/grid?promptId=${customLink.promptId}&tid=${tid}`);
                  
                  // Only remove the pending tracking ID after successful navigation
                  localStorage.removeItem('pending_tracking_id');
                } catch (error) {
                  console.error('Error loading prompt:', error);
                  // Navigate to grid anyway, the prompt will be loaded there
                  navigate(`/grid?promptId=${customLink.promptId}&tid=${tid}`);
                  
                  // Only remove the pending tracking ID after successful navigation
                  localStorage.removeItem('pending_tracking_id');
                }
                return;
              }
              
              // Otherwise go to tool directly if that's the current URL
              if (window.location.pathname === '/tool') {
                localStorage.removeItem('pending_tracking_id');
                return;
              }
              
              // If no prompt ID and not on tool page, go to home
              navigate(`/?tid=${tid}`);
              localStorage.removeItem('pending_tracking_id');
            } else {
              // If no custom link info found, just remove the pending ID
              localStorage.removeItem('pending_tracking_id');
            }
          } catch (error) {
            console.error('Error processing custom link after login:', error);
            localStorage.removeItem('pending_tracking_id');
          }
        } else {
          console.error('Failed to update user with tracking ID - server returned an error');
        }
      } catch (error) {
        console.error('Failed to update user with tracking ID:', error);
      }
    } else if (authState.isAuthenticated && !authState.isLoading) {
      console.log('User authenticated but no pending tracking ID found');
    }
  });

  // Initialize evidence store synchronization
  let evidenceStoreUnsubscribe;

  onMount(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle tracking ID in URL
    const tid = urlParams.get("tid");
    if (tid) {
      // Store tracking ID in local storage for later use if user registers/logs in
      localStorage.setItem('pending_tracking_id', tid);
      
      // Only process custom link for non-authenticated users
      // For authenticated users, the auth subscriber will handle it
      if (!$authStore.isAuthenticated) {
        // Fetch custom link info from API
        const customLink = await fetchCustomLinkByTrackingId(tid);
        
        if (customLink) {
          // Set tracking info in the store
          trackingInfo.set({
            trackingId: tid,
            promptId: customLink.promptId,
            name: customLink.name
          });
          
          // If there's a preset prompt, redirect to /grid with that prompt
          if (customLink.promptId) {
            // First load the prompt into the store
            try {
              // Check if the function exists in promptStore, otherwise use fallback
              if (typeof promptStore.fetchPromptById === 'function') {
                await promptStore.fetchPromptById(customLink.promptId);
              } else {
                // Use fallback implementation
                await fetchPromptByIdFallback(customLink.promptId);
              }
              
              // Then navigate to grid with the prompt pre-selected
              navigate(`/grid?promptId=${customLink.promptId}&tid=${tid}`);
            } catch (error) {
              console.error('Error loading prompt:', error);
              // Navigate to grid anyway, the prompt will be loaded there
              navigate(`/grid?promptId=${customLink.promptId}&tid=${tid}`);
            }
            return;
          }
          
          // Otherwise go to tool directly if that's the current URL
          if (window.location.pathname === '/tool') {
            return;
          }
          
          // If no prompt ID and not on tool page, go to home
          navigate(`/?tid=${tid}`);
        }
      } else {
        console.log('User already authenticated, tracking ID will be processed by auth subscriber');
      }
    } else {
      // No tracking ID provided - ensure tracking info is reset to defaults
      trackingInfo.set({
        trackingId: "",
        promptId: null,
        name: ""
      });
    }
    
    // Backward compatibility for classroom parameters
    const room = urlParams.get("room");
    const id = urlParams.get("id");
    if (room && id) {
      // Create classroom tracking ID format
      const classroomTrackingId = `classroom-${room}-${id}`;
      trackingInfo.set({
        trackingId: classroomTrackingId,
        promptId: null,
        name: `Classroom ${room}`
      });
      localStorage.setItem('pending_tracking_id', classroomTrackingId);
    }
    
    // Check authentication status when app loads
    authStore.checkAuth();

    // Initialize the evidence store to sync with the story store
    try {
      evidenceStoreUnsubscribe = await initializeEvidenceStore();
      console.log("Evidence store initialized and synced with story store");
    } catch (error) {
      console.error("Failed to initialize evidence store:", error);
    }
  });

  onDestroy(() => {
    // Clean up auto-save timer and subscriptions
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    storyUnsubscribe();
    authUnsubscribe();
    if (evidenceStoreUnsubscribe) evidenceStoreUnsubscribe();
  });

  export let url = "";
</script>

<Router {url}>
  <main>
    <Route path="/" component={HomePage} />
    
    <!-- Protected routes -->
    <Route path="/grid" let:params>
      <AuthGuard path="/grid">
        <StoryGridPage />
      </AuthGuard>
    </Route>
    
    <Route path="/tool" let:params>
      <AuthGuard path="/tool">
        <StorySlidePage />
      </AuthGuard>
    </Route>
    
    <Route path="/researcher" let:params>
      <AuthGuard path="/researcher">
        <StoryResearcherPage />
      </AuthGuard>
    </Route>

    <!-- Admin routes -->
    <Route path="/manage-evidence" let:params>
      <AuthGuard path="/manage-evidence" adminRequired={true}>
        <ManageEvidencePage />
      </AuthGuard>
    </Route>
  </main>
</Router>

<ToastContainer />

<style>
  main {
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
</style>
